import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Tip } from '@/components/shared/tip';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { api } from '@/lib/api';
import { memberGenderListboxOptions } from '@/lib/gender';
import {
  maritalStatusFilterOptions,
  receptionMethodFilterOptions,
} from '@/lib/member-filter-options';

export interface MessageListCriteriaForm {
  memberTypes: string[];
  statuses: string[];
  maritalStatuses: string[];
  genders: string[];
  birthMonths: string[];
  minAge: string;
  maxAge: string;
  departmentIds: string[];
  unitIds: string[];
  groupIds: string[];
  joinedAfter: string | null;
  joinedBefore: string | null;
  hasEmail: string | null;
  hasPhone: string | null;
  isCommunicant: string | null;
  isDistantMember: string | null;
  hasDisability: string | null;
  receptionMethods: string[];
}

export const emptyMessageListCriteriaForm = (): MessageListCriteriaForm => ({
  memberTypes: [],
  statuses: [],
  maritalStatuses: [],
  genders: [],
  birthMonths: [],
  minAge: '',
  maxAge: '',
  departmentIds: [],
  unitIds: [],
  groupIds: [],
  joinedAfter: null,
  joinedBefore: null,
  hasEmail: null,
  hasPhone: null,
  isCommunicant: null,
  isDistantMember: null,
  hasDisability: null,
  receptionMethods: [],
});

export const boolToChoice = (value: boolean | undefined): string | null => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return null;
};

export const choiceToBool = (value: string | null): boolean | undefined => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
};

const MONTH_VALUES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'] as const;

const parseAge = (value: string): number | null => {
  if (!value.trim()) return null;
  if (!/^\d{1,3}$/.test(value.trim())) return Number.NaN;
  const n = Number(value.trim());
  if (!Number.isInteger(n) || n < 0 || n > 150) return Number.NaN;
  return n;
};

export const validateMessageListCriteriaForm = (form: MessageListCriteriaForm) => {
  const parsedMinAge = parseAge(form.minAge);
  const parsedMaxAge = parseAge(form.maxAge);
  const ageFormatInvalid =
    (form.minAge.trim() !== '' && Number.isNaN(parsedMinAge)) ||
    (form.maxAge.trim() !== '' && Number.isNaN(parsedMaxAge));
  const ageRangeInvalid =
    parsedMinAge != null &&
    parsedMaxAge != null &&
    !Number.isNaN(parsedMinAge) &&
    !Number.isNaN(parsedMaxAge) &&
    parsedMinAge > parsedMaxAge;
  const joinRangeInvalid = Boolean(
    form.joinedAfter && form.joinedBefore && form.joinedAfter > form.joinedBefore,
  );
  return {
    invalid: ageFormatInvalid || ageRangeInvalid || joinRangeInvalid,
    ageFormatInvalid,
    ageRangeInvalid,
    joinRangeInvalid,
    parsedMinAge,
    parsedMaxAge,
  };
};

export const messageListCriteriaFromForm = (
  form: MessageListCriteriaForm,
): Record<string, unknown> => {
  const validation = validateMessageListCriteriaForm(form);
  if (validation.invalid) return {};

  const criteria: Record<string, unknown> = {};
  if (form.memberTypes.length) criteria.memberTypes = form.memberTypes;
  if (form.statuses.length) criteria.statuses = form.statuses;
  if (form.maritalStatuses.length) criteria.maritalStatuses = form.maritalStatuses;
  if (form.genders.length) criteria.genders = form.genders;
  if (form.birthMonths.length) criteria.birthMonths = form.birthMonths.map(Number);
  if (validation.parsedMinAge != null && !Number.isNaN(validation.parsedMinAge)) {
    criteria.minAge = validation.parsedMinAge;
  }
  if (validation.parsedMaxAge != null && !Number.isNaN(validation.parsedMaxAge)) {
    criteria.maxAge = validation.parsedMaxAge;
  }
  if (form.departmentIds.length) criteria.departmentIds = form.departmentIds;
  if (form.unitIds.length) criteria.unitIds = form.unitIds;
  if (form.groupIds.length) criteria.groupIds = form.groupIds;
  if (form.joinedAfter) criteria.joinedAfter = form.joinedAfter;
  if (form.joinedBefore) criteria.joinedBefore = form.joinedBefore;
  if (form.hasEmail === 'yes') criteria.hasEmail = true;
  if (form.hasEmail === 'no') criteria.hasEmail = false;
  if (form.hasPhone === 'yes') criteria.hasPhone = true;
  if (form.hasPhone === 'no') criteria.hasPhone = false;

  const communicant = choiceToBool(form.isCommunicant);
  if (communicant !== undefined) criteria.isCommunicant = communicant;
  const distant = choiceToBool(form.isDistantMember);
  if (distant !== undefined) criteria.isDistantMember = distant;
  const disability = choiceToBool(form.hasDisability);
  if (disability !== undefined) criteria.hasDisability = disability;
  if (form.receptionMethods.length) criteria.receptionMethods = form.receptionMethods;

  return criteria;
};

export const messageListCriteriaToForm = (
  criteria: Record<string, unknown> | null | undefined,
): MessageListCriteriaForm => ({
  ...emptyMessageListCriteriaForm(),
  memberTypes: (criteria?.memberTypes as string[] | undefined) ?? [],
  statuses: (criteria?.statuses as string[] | undefined) ?? [],
  maritalStatuses: (criteria?.maritalStatuses as string[] | undefined) ?? [],
  genders: (criteria?.genders as string[] | undefined) ?? [],
  birthMonths: ((criteria?.birthMonths as number[] | undefined) ?? []).map(String),
  minAge: criteria?.minAge != null ? String(criteria.minAge) : '',
  maxAge: criteria?.maxAge != null ? String(criteria.maxAge) : '',
  departmentIds: (criteria?.departmentIds as string[] | undefined) ?? [],
  unitIds: (criteria?.unitIds as string[] | undefined) ?? [],
  groupIds: (criteria?.groupIds as string[] | undefined) ?? [],
  joinedAfter: (criteria?.joinedAfter as string | undefined) ?? null,
  joinedBefore: (criteria?.joinedBefore as string | undefined) ?? null,
  hasEmail: boolToChoice(criteria?.hasEmail as boolean | undefined),
  hasPhone: boolToChoice(criteria?.hasPhone as boolean | undefined),
  isCommunicant: boolToChoice(criteria?.isCommunicant as boolean | undefined),
  isDistantMember: boolToChoice(criteria?.isDistantMember as boolean | undefined),
  hasDisability: boolToChoice(criteria?.hasDisability as boolean | undefined),
  receptionMethods: (criteria?.receptionMethods as string[] | undefined) ?? [],
});

interface MessageListCriteriaFieldsProps {
  value: MessageListCriteriaForm;
  onChange: (next: MessageListCriteriaForm) => void;
  departments: Array<{ id: string; name: string }>;
  groups: Array<{ id: string; name: string }>;
}

export const MessageListCriteriaFields = ({
  value,
  onChange,
  departments,
  groups,
}: MessageListCriteriaFieldsProps) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tPeople } = useTranslation('people');

  const set = <K extends keyof MessageListCriteriaForm>(
    key: K,
    next: MessageListCriteriaForm[K],
  ) => onChange({ ...value, [key]: next });

  const validation = validateMessageListCriteriaForm(value);

  const units = useQuery({
    queryKey: ['people', 'department-units', 'list-criteria', value.departmentIds],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; label: string; name: string }> }>(
        `/people/department-units?pageSize=100&departmentIds=${encodeURIComponent(value.departmentIds.join(','))}`,
      ),
    enabled: value.departmentIds.length > 0,
  });

  const yesNoListbox = (
    fieldValue: string | null,
    onFieldChange: (next: string | null) => void,
  ) => (
    <Listbox
      value={fieldValue}
      onChange={onFieldChange}
      options={[
        { value: 'yes', label: tCommon('boolean.yes') },
        { value: 'no', label: tCommon('boolean.no') },
      ]}
      clearable
      clearLabel={tCommon('action.clearSelection')}
      popoverPortal="dialog"
    />
  );

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div>
        <Label>{t('lists.memberTypes')}</Label>
        <MultiSelectListbox
          value={value.memberTypes}
          onChange={(next) => set('memberTypes', next)}
          options={[
            { value: 'member', label: tPeople('members.type.member') },
            { value: 'visitor', label: tPeople('members.type.visitor') },
            { value: 'first_timer', label: tPeople('members.type.first_timer') },
          ]}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.statuses')}</Label>
        <MultiSelectListbox
          value={value.statuses}
          onChange={(next) => set('statuses', next)}
          options={[
            { value: 'active', label: tPeople('members.status.active') },
            { value: 'inactive', label: tPeople('members.status.inactive') },
            { value: 'transferred', label: tPeople('members.status.transferred') },
            { value: 'deceased', label: tPeople('members.status.deceased') },
          ]}
          popoverPortal="dialog"
        />
        <p className="mt-1 text-caption text-fg-secondary">{t('lists.statusesHelp')}</p>
      </div>
      <div>
        <Label>{t('lists.maritalStatuses')}</Label>
        <MultiSelectListbox
          value={value.maritalStatuses}
          onChange={(next) => set('maritalStatuses', next)}
          options={maritalStatusFilterOptions(tPeople)}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.genders')}</Label>
        <MultiSelectListbox
          value={value.genders}
          onChange={(next) => set('genders', next)}
          options={memberGenderListboxOptions(tPeople)}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.receptionMethods')}</Label>
        <MultiSelectListbox
          value={value.receptionMethods}
          onChange={(next) => set('receptionMethods', next)}
          options={receptionMethodFilterOptions(tPeople)}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.birthMonths')}</Label>
        <MultiSelectListbox
          value={value.birthMonths}
          onChange={(next) => set('birthMonths', next)}
          options={MONTH_VALUES.map((month) => ({
            value: month,
            label: t(`lists.months.${month}`),
          }))}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.ageRange')}</Label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="min-age" className="text-caption text-fg-secondary">
              {t('lists.minAge')}
            </Label>
            <Input
              id="min-age"
              inputMode="numeric"
              value={value.minAge}
              onChange={(event) => set('minAge', event.target.value)}
              invalid={validation.ageFormatInvalid || validation.ageRangeInvalid}
            />
          </div>
          <div>
            <Label htmlFor="max-age" className="text-caption text-fg-secondary">
              {t('lists.maxAge')}
            </Label>
            <Input
              id="max-age"
              inputMode="numeric"
              value={value.maxAge}
              onChange={(event) => set('maxAge', event.target.value)}
              invalid={validation.ageFormatInvalid || validation.ageRangeInvalid}
            />
          </div>
        </div>
        {validation.ageFormatInvalid ? (
          <p className="mt-1 text-caption text-danger">{t('lists.ageInvalid')}</p>
        ) : null}
        {validation.ageRangeInvalid ? (
          <p className="mt-1 text-caption text-danger">{t('lists.ageRangeInvalid')}</p>
        ) : null}
      </div>
      <div>
        <Label>{t('lists.departments')}</Label>
        <MultiSelectListbox
          value={value.departmentIds}
          onChange={(next) => {
            onChange({
              ...value,
              departmentIds: next,
              unitIds: [],
            });
          }}
          options={departments.map((row) => ({ value: row.id, label: row.name }))}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.units')}</Label>
        <MultiSelectListbox
          value={value.unitIds}
          onChange={(next) => set('unitIds', next)}
          options={(units.data?.data ?? []).map((row) => ({
            value: row.id,
            label: row.label ?? row.name,
          }))}
          placeholder={
            value.departmentIds.length > 0 ? undefined : t('lists.selectDepartmentsFirst')
          }
          disabled={value.departmentIds.length === 0}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.groups')}</Label>
        <MultiSelectListbox
          value={value.groupIds}
          onChange={(next) => set('groupIds', next)}
          options={groups.map((row) => ({ value: row.id, label: row.name }))}
          popoverPortal="dialog"
        />
      </div>
      <div>
        <Label>{t('lists.joinPeriod')}</Label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-caption text-fg-secondary">{t('lists.joinedAfter')}</Label>
            <DatePicker
              value={value.joinedAfter}
              onChange={(next) => set('joinedAfter', next)}
              clearable
              clearLabel={tCommon('action.clearSelection')}
              max={value.joinedBefore ?? undefined}
              invalid={validation.joinRangeInvalid}
              popoverPortal="dialog"
            />
          </div>
          <div>
            <Label className="text-caption text-fg-secondary">{t('lists.joinedBefore')}</Label>
            <DatePicker
              value={value.joinedBefore}
              onChange={(next) => set('joinedBefore', next)}
              clearable
              clearLabel={tCommon('action.clearSelection')}
              min={value.joinedAfter ?? undefined}
              invalid={validation.joinRangeInvalid}
              popoverPortal="dialog"
            />
          </div>
        </div>
        {validation.joinRangeInvalid ? (
          <p className="mt-1 text-caption text-danger">{t('lists.joinRangeInvalid')}</p>
        ) : null}
      </div>
      <div>
        <Label>{t('lists.contactFilters')}</Label>
        <Tip tipId="messaging.listContactFilters" />
        <div className="mt-1 grid grid-cols-2 gap-2">
          <div>
            <Label className="text-caption text-fg-secondary">{t('lists.hasEmail')}</Label>
            {yesNoListbox(value.hasEmail, (next) => set('hasEmail', next))}
          </div>
          <div>
            <Label className="text-caption text-fg-secondary">{t('lists.hasPhone')}</Label>
            {yesNoListbox(value.hasPhone, (next) => set('hasPhone', next))}
          </div>
        </div>
      </div>
      <div>
        <Label>{t('lists.profileFilters')}</Label>
        <div className="mt-1 grid grid-cols-2 gap-2">
          <div>
            <Label className="text-caption text-fg-secondary">{t('lists.isCommunicant')}</Label>
            {yesNoListbox(value.isCommunicant, (next) => set('isCommunicant', next))}
          </div>
          <div>
            <Label className="text-caption text-fg-secondary">{t('lists.isDistantMember')}</Label>
            {yesNoListbox(value.isDistantMember, (next) => set('isDistantMember', next))}
          </div>
          <div>
            <Label className="text-caption text-fg-secondary">{t('lists.hasDisability')}</Label>
            {yesNoListbox(value.hasDisability, (next) => set('hasDisability', next))}
          </div>
        </div>
      </div>
    </div>
  );
};
